import React, {useEffect, useRef, useState} from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import "../../css/ProductDetailPage1.css";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import { useCart } from "../../context/cart";
import axios from "axios";
import { useAuth } from "../../context/auth";
import { toast } from "react-hot-toast";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import PopUp from "../../components/PopUp";
import Loader from "../../components/Loader";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import StraightenIcon from "@mui/icons-material/Straighten";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const { addToCart } = useCart();
  const [selectedSize, setSelectedSize] = useState("");
  const [wishlist, setWishlist] = useState([]);
  const [auth] = useAuth();
  const [related, setRelated] = useState([]);
  const [showPopUp, setShowPopUp] = useState(false);
  const [btnName, setBtnName] = useState("");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [availableImages, setAvailableImages] = useState([]);

  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const apiUrl = `${process.env.REACT_APP_API}`;
  const sizechart = "https://subcategories-image-upload-aws-1.s3.amazonaws.com/Size+Chart.png";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  const openFullImage = (image) => {
    setModalImage(image);
    setOpenModal(true);
  };

  const handlePrevImage = () => {
    setIsVideoReady(false); // ADD THIS
    setSelectedImageIndex((prev) =>
        prev === 0 ? availableImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setIsVideoReady(false); // ADD THIS
    setSelectedImageIndex((prev) =>
        prev === availableImages.length - 1 ? 0 : prev + 1
    );
  };

  const handleSizeSelection = (size) => {
    setSelectedSize(size);
  };

  const handleAddToCart = (product, size) => {
    if (product.productSize.sizes.length > 0 && !selectedSize) {
      toast.error("Please select a size");
      return;
    }

    const price = product.salePrice > 0 ? product.salePrice : product.productPrice;

    const chosenSize = product.productSize.sizes.find(
        (sizeObj) => sizeObj.label === size
    );

    const availableQty = chosenSize ? chosenSize.quantity : 1;

    addToCart(product, size || "Free Size", 1, price, availableQty);
  };

  const filterProductByQuantity = (products) => {
    return products.filter((p) => {
      return p.productSize.sizes.some((size) => size.quantity > 0);
    });
  };

  const loadRelated = async (productId, categoryId) => {
    try {
      const { data } = await axios.get(
          `${apiUrl}/related-products/${productId}/${categoryId}`
      );
      setRelated(filterProductByQuantity(data));
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${apiUrl}/product/${id}`);
        const data = await response.json();
        setProduct(data);

        // Build available images array
        const images = [];
        if (data.images.front) images.push({ url: data.images.front, label: "Front" });
        if (data.images.back) images.push({ url: data.images.back, label: "Back" });
        if (data.images.left) images.push({ url: data.images.left, label: "Left" });
        if (data.images.right) images.push({ url: data.images.right, label: "Right" });

        // Add video as last slide if available
        if (data.videoUrl) images.push({ url: data.videoUrl, label: "Video", type: "video" });

        setAvailableImages(images);
        setSelectedImageIndex(0);

        await loadRelated(data._id, data.subCategory);
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, [id]);

  const fetchWishlist = async () => {
    try {
      if (auth && auth.currentUser && auth.currentUser.id) {
        const response = await axios.get(`${apiUrl}/wishlist`);
        setWishlist(response.data.products);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchWishlist();
  }, [auth]);

  const isInWishlist = (productId) => {
    return wishlist.some((item) => item._id === productId);
  };

  const addToWishlist = async (productId) => {
    try {
      await axios.post(`${apiUrl}/wishlist/add`, { productId });
      toast.success("Added to wishlist");
      await fetchWishlist();
    } catch (error) {
      console.error(error);
      toast.error("Error adding to wishlist");
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      if (!auth) return;
      await axios.delete(`${apiUrl}/wishlist/delete/${productId}`);
      toast.success("Removed from Wishlist");
      await fetchWishlist();
    } catch (err) {
      console.error(err.message);
      toast.error("Error removing from wishlist");
    }
  };

  const handleRelatedProductClick = () => {
    // Scroll to top when clicking related product
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  useEffect(() => {
    if (availableImages.length === 0) return;

    const hasVideo = availableImages.some(img => img.type === "video");
    if (!hasVideo) return;
    if (selectedImageIndex !== 0) return;

    const videoIndex = availableImages.findIndex(img => img.type === "video");

    const timer = setTimeout(() => {
      if (videoIndex !== -1) {
        setSelectedImageIndex(videoIndex);
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, [selectedImageIndex, availableImages]);

  const handleBackClick = () => {
    navigate(-1);
  };
  if (!product) {
    return <Loader />;
  }



  return (
      <>
        <Header />
        <div className="product-detail-page">
          <div className="container py-4">
            {/* Back Button */}
            <button className="back-button mb-4" onClick={handleBackClick}>
              ← Back to Products
            </button>

            <div className="row g-4">
              {/* Image Gallery Section */}
              <div className="col-lg-6">
                <div className="image-gallery">
                  {/* Image Slider */}
                  <div className="image-slider">
                    <div className="main-image-container">
                      {availableImages[selectedImageIndex] && availableImages[selectedImageIndex].type === "video" ? (
                          <>
                            <video
                                key={availableImages[selectedImageIndex].url}
                                src={availableImages[selectedImageIndex].url}
                                className="main-image"
                                autoPlay
                                muted
                                loop
                                playsInline
                                ref={videoRef}
                                controls={false}
                                onCanPlay={() => setIsVideoReady(true)}
                                style={{
                                  background: "#000",
                                  cursor: "default",
                                  opacity: isVideoReady ? 1 : 0,
                                  transition: "opacity 0.4s ease-in-out"
                                }}
                            />
                            <div className="custom-video-controls">
                              <button
                                  className="custom-video-btn"
                                  onClick={() => {
                                    const vid = videoRef.current;
                                    if (vid.paused) {
                                      vid.play();
                                      setIsVideoPlaying(true);
                                    } else {
                                      vid.pause();
                                      setIsVideoPlaying(false);
                                    }
                                  }}
                              >
                                {isVideoPlaying ? "⏸" : "▶"}
                              </button>
                              <button
                                  className="custom-video-btn"
                                  onClick={() => {
                                    videoRef.current.muted = !videoRef.current.muted;
                                    setIsVideoMuted(!isVideoMuted);
                                  }}
                              >
                                {isVideoMuted ? "🔇" : "🔊"}
                              </button>
                            </div>
                          </>
                      ) : (
                          <>
                            <img
                                src={availableImages[selectedImageIndex] && availableImages[selectedImageIndex].url}
                                alt={product.productName}
                                className="main-image"
                                onClick={() => openFullImage(availableImages[selectedImageIndex] && availableImages[selectedImageIndex].url)}
                            />
                            <button
                                className="zoom-button"
                                onClick={() => openFullImage(availableImages[selectedImageIndex] && availableImages[selectedImageIndex].url)}
                            >
                              <ZoomInIcon />
                            </button>
                          </>
                      )}

                  {/* Navigation Arrows - Only show if more than 1 image */}
                  {availableImages.length > 1 && (
                      <>
                        <button className="slider-arrow arrow-left" onClick={handlePrevImage}>
                          ‹
                        </button>
                        <button className="slider-arrow arrow-right" onClick={handleNextImage}>
                          ›
                        </button>
                      </>
                  )}

                  {/* Image Counter */}
                  {availableImages.length > 1 && (
                      <div className="image-counter">
                        {selectedImageIndex + 1} / {availableImages.length}
                      </div>
                  )}
                </div>

                    {availableImages.length > 1 && (
                        <div className="dot-indicators">
                          {availableImages.map((media, index) => (
                              <button
                                  key={index}
                                  className={`dot ${selectedImageIndex === index ? "active" : ""} ${media.type === "video" ? "video-dot" : ""}`}
                                  onClick={() => {
                                    setIsVideoReady(false); // ADD THIS
                                    setSelectedImageIndex(index);
                                  }}
                              >
                                {media.type === "video" && (
                                    <PlayCircleIcon style={{ fontSize: "0.85rem" }} />
                                )}
                              </button>
                          ))}
                        </div>
                    )}
              </div>
            </div>
          </div>

          {/* Product Info Section */}
          <div className="col-lg-6">
            <div className="product-info-section">
              {/* Product Name */}
              <h1 className="product-detail-name">{product.productName}</h1>

              {/* Price */}
              <div className="product-detail-pricing">
                {product.salePrice > 0 ? (
                    <>
                      <span className="detail-sale-price">${product.salePrice}</span>
                      <span className="detail-original-price">${product.productPrice}</span>
                      <span className="detail-discount-badge">
                        <LocalOfferIcon fontSize="small" className="me-1"/>
                        {Math.round(
                            ((product.productPrice - product.salePrice) /
                                product.productPrice) *
                            100
                        )}
                        % OFF
                      </span>
                    </>
                ) : (
                    <span className="detail-current-price">${product.productPrice}</span>
                )}
              </div>

              {/* Size Selection */}
              <div className="size-section">
                <div className="section-header">
                  <h6 className="section-title">Select Size:</h6>
                  {product.subCategory === "64524e6af31946bee9690f78" && (
                      <button
                          className="size-chart-link"
                          onClick={() => openFullImage(sizechart)}
                      >
                        <StraightenIcon fontSize="small" className="me-1"/>
                        Size Chart
                      </button>
                  )}
                </div>

                {product.productSize.sizes.length === 0 ? (
                    <div className="free-size-badge">
                          <CheckCircleIcon fontSize="small" className="me-1" />
                          Free Size
                        </div>
                    ) : (
                        <div className="size-buttons">
                          {product.productSize.sizes.map(
                              (size) =>
                                  size.quantity > 0 && (
                                      <button
                                          key={size._id}
                                          className={`size-btn ${
                                              selectedSize === size.label ? "active" : ""
                                          }`}
                                          onClick={() => handleSizeSelection(size.label)}
                                      >
                                        {size.label}
                                        {selectedSize === size.label && (
                                            <CheckCircleIcon className="check-icon" />
                                        )}
                                      </button>
                                  )
                          )}
                        </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="action-buttons">
                    <button
                        className="btn-add-to-cart"
                        onClick={() => {
                          handleAddToCart(product, selectedSize);
                        }}
                    >
                      <ShoppingCartIcon className="me-2" />
                      Add to Cart
                    </button>

                    <button
                        className={`btn-wishlist ${isInWishlist(product._id) ? "active" : ""}`}
                        onClick={() => {
                          if (!localStorage.getItem("auth")) {
                            setShowPopUp(true);
                            setBtnName("wishlist");
                            return;
                          }
                          if (isInWishlist(product._id)) {
                            removeFromWishlist(product._id);
                          } else {
                            addToWishlist(product._id);
                          }
                        }}
                    >
                      {isInWishlist(product._id) ? (
                          <FavoriteIcon className="me-2" />
                      ) : (
                          <FavoriteBorderIcon className="me-2" />
                      )}
                      {isInWishlist(product._id) ? "In Wishlist" : "Add to Wishlist"}
                    </button>
                  </div>

                  {/* Description */}
                  <div className="description-section">
                    <h6 className="section-title">Product Description:</h6>
                    <p className="product-description">{product.productDescription}</p>
                  </div>



                </div>
              </div>
            </div>

            {/* Related Products Section */}
            {related.length > 0 && (
                <div className="related-products-section">
                  <h3 className="related-title">You May Also Like</h3>
                  <div className="related-products-grid">
                    {related.map((relatedProduct) => (
                        <div key={relatedProduct._id} className="related-product-card">
                          <div className="related-image-container">
                            <Link
                                to={`/products/${relatedProduct._id}`}
                                onClick={handleRelatedProductClick}
                            >
                              <img
                                  src={relatedProduct.images.front}
                                  alt={relatedProduct.productName}
                                  className="related-product-image"
                                  loading="lazy"
                              />
                            </Link>

                            {/* Sale Badge */}
                            {relatedProduct.salePrice > 0 && (
                                <div className="related-sale-badge">
                                  <LocalOfferIcon fontSize="small" className="me-1" />
                                  {Math.round(
                                      ((relatedProduct.productPrice - relatedProduct.salePrice) /
                                          relatedProduct.productPrice) *
                                      100
                                  )}
                                  % OFF
                                </div>
                            )}

                            {/* Wishlist Button */}
                            <button
                                className={`related-wishlist-btn ${
                                    isInWishlist(relatedProduct._id) ? "active" : ""
                                }`}
                                onClick={() => {
                                  if (!localStorage.getItem("auth")) {
                                    toast.error("Please log in to add to wishlist");
                                    return;
                                  }
                                  if (isInWishlist(relatedProduct._id)) {
                                    removeFromWishlist(relatedProduct._id);
                                  } else {
                                    addToWishlist(relatedProduct._id);
                                  }
                                }}
                            >
                              <FavoriteIcon
                                  style={{
                                    fontSize: "1.3rem",
                                    color: isInWishlist(relatedProduct._id) ? "#c70044" : "white",
                                  }}
                              />
                            </button>
                          </div>

                          <div className="related-product-info">
                            <Link
                                to={`/products/${relatedProduct._id}`}
                                className="related-product-link"
                                onClick={handleRelatedProductClick}
                            >
                              <h5 className="related-product-name">
                                {relatedProduct.productName}
                              </h5>
                            </Link>

                            <div className="related-product-pricing">
                              {relatedProduct.salePrice > 0 ? (
                                  <>
                            <span className="related-sale-price">
                              ${relatedProduct.salePrice}
                            </span>
                                    <span className="related-original-price">
                              ${relatedProduct.productPrice}
                            </span>
                                  </>
                              ) : (
                                  <span className="related-current-price">
                            ${relatedProduct.productPrice}
                          </span>
                              )}
                            </div>
                          </div>
                        </div>
                    ))}
                  </div>
                </div>
            )}
          </div>
        </div>

        {/* Modal for Full Image */}
        {openModal && (
            <Modal open={openModal} onClose={() => setOpenModal(false)} center>
              <img
                  src={modalImage}
                  alt={product.productName}
                  style={{ width: "100%", height: "auto", maxHeight: "90vh", objectFit: "contain" }}
              />
            </Modal>
        )}

        {/* Login Popup */}
        {showPopUp && (
            <PopUp
                loginButtonVisible={true}
                onLoginClick={() => {
                  navigate("/login", {
                    state: { from: location.pathname },
                  });
                }}
                onCloseClick={() => {
                  setShowPopUp(false);
                }}
            >
              Please Login to add products to {btnName}.
            </PopUp>
        )}

        <Footer />
      </>
  );
};

export default ProductDetailPage;